import { FitScoreSummaryResponse } from '@/data/usecases/content/remote-user-content-summary'
import {
  FitScoreCategory,
  FitScoreSkillCompatibility,
  FitScoreSkillKey,
  FitScoreSkillsCompatibility,
  FitScoreSummary,
  FormattedFitScore,
  FormattedFitScoreSkill,
  ProductFitScoreResponse
} from './product-fit-score-service-types'

export const FitScoreLevel = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const

export type FitScoreLevel = (typeof FitScoreLevel)[keyof typeof FitScoreLevel]

/**
 * Ordem canônica de exibição das skills — as de educação e formação primeiro, as de idiomas
 * depois. É ordem de apresentação, não agrupamento por categoria: quais delas aparecem sai
 * sempre do payload.
 */
const ALL_SKILL_KEYS: FitScoreSkillKey[] = [
  'professionalRole',
  'seniority',
  'language',
  'dedication',
  'modal',
  'classType'
]

/**
 * Categorias cujo cálculo depende da feature de preferências de estudo.
 *
 * O critério não é "é idiomas": é se o cálculo LÊ os dados que o usuário declara em
 * `/user/learning-preferences` — disponibilidade semanal, formato e tipo de aula. Onde lê, a
 * variação só pode aparecer com a feature ligada: os links "editá-la" apontam para essa rota,
 * guardada pela mesma flag, e sem ela a tela comentaria uma incompatibilidade que o usuário não
 * tem como corrigir.
 *
 * Mapeado categoria a categoria: uma categoria nova que leia preferências precisa
 * aparecer aqui explicitamente. Com um ternário `=== LANGUAGE ? ... : ...` ela herdaria "não
 * depende" calada e vazaria com a feature off.
 */
const CATEGORY_REQUIRES_LEARNING_PREFERENCES: Record<FitScoreCategory, boolean> = {
  COURSE: false,
  UNDERGRADUATE: false,
  GRADUATE: false,
  MENTORING: false,
  TECHNICAL: false,
  HIGH_SCHOOL: false,
  EXTENSION: false,
  LANGUAGE: true
}

/**
 * Normaliza `category` na fronteira do payload, para a forma canônica do contrato.
 *
 * O contrato pede `SCREAMING_SNAKE_CASE`, mas o valor chega do fio como texto livre: o
 * interceptor HTTP camelizza chaves, nunca valores, então a caixa é a que o serializador do enum
 * do backend produziu. Sem normalizar, `"language"` não casa em mapa nenhum e o `?? fallback` dos
 * consumidores transforma isso em dois defeitos silenciosos — a variação de idiomas renderizada
 * com o corpo de educação, e `requiresLearningPreferences` devolvendo `false`, que é o gate da
 * flag falhando ABERTO. Nenhum dos dois quebra o build: o valor é afirmado como `FitScoreCategory`
 * na borda, e `tsc` não vê o que vem no fio.
 *
 * Normaliza sem validar contra as categorias conhecidas, de propósito. Categoria que o front
 * ainda não conhece sai daqui intacta (só com a caixa corrigida), porque `getFitScoreVariant` e
 * `requiresLearningPreferences` já a tratam pelo `?? fallback` — descartá-la para `null` daria o
 * mesmo resultado em tela e perderia o valor para depuração e análise.
 */
export const parseFitScoreCategory = (raw: unknown): FitScoreCategory | null => {
  if (typeof raw !== 'string') return null

  const canonical = raw
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2') // highSchool → high_School
    .replace(/[\s-]+/g, '_') // "high school" | high-school → high_school
    .toUpperCase()

  return (canonical || null) as FitScoreCategory | null
}

/**
 * Se a resposta descreve uma compatibilidade que só faz sentido com a feature de preferências
 * de estudo no ar. É o que o loader consulta para esconder a variação com a flag desligada.
 *
 * `null` é a resposta legada (educação e formação): não depende.
 *
 * Categoria desconhecida cai em `false`, e isso é coerente e não um furo: o fallback de
 * variação é `education`, cujos links de edição vão para `/user/profile` e
 * `/user/interest-areas-skills` — nenhum deles guardado pela flag. Uma categoria nova que leia
 * preferências precisa da entrada no mapa; sem ela a UI a descreve com o vocabulário de
 * educação, que não referencia rota bloqueada.
 */
export const requiresLearningPreferences = (category: FitScoreCategory | null): boolean => {
  if (!category) return false

  const knownCategories = CATEGORY_REQUIRES_LEARNING_PREFERENCES as Partial<
    Record<FitScoreCategory, boolean>
  >
  return knownCategories[category] ?? false
}

/**
 * Critérios que o backend avaliou nesta resposta, na ordem canônica — o que o painel "Como
 * calculamos sua compatibilidade?" explica.
 *
 * Lê o payload, nunca a categoria: o backend popula o grupo da categoria e anula o outro, então
 * filtrar por presença dá o mesmo resultado que um mapa por categoria daria — sem depender de
 * `category` vir correta, e acertando de graça uma categoria nova com critérios próprios.
 */
export const getEvaluatedSkillKeys = (skills: FitScoreSkillsCompatibility): FitScoreSkillKey[] =>
  ALL_SKILL_KEYS.filter(key => skills[key] != null)

/**
 * Skills incompatíveis, na ordem canônica.
 *
 * Lê o payload, não a categoria: a skill entra na lista quando o backend a avaliou (chave
 * presente) e o nível não é HIGH. Consultar o mapa por categoria aqui só criaria uma segunda
 * fonte de verdade — com `category` errada ou desconhecida, a lista sairia vazia em silêncio.
 */
const filterIncompatibleSkills = (skills: FitScoreSkillsCompatibility): FormattedFitScoreSkill[] =>
  ALL_SKILL_KEYS.flatMap(key => {
    const data = skills[key] as FitScoreSkillCompatibility | null | undefined
    if (!data || data.compatibility === FitScoreLevel.HIGH) return []
    return [{ key, data }]
  })

/**
 * Formato compact `{ level, approved }` usado em listas (search, recommended, summary).
 * Carregar o `skillsCompatibility` completo em todos os itens de lista seria caro;
 * esse helper centraliza a transformação que estava duplicada em 4 lugares.
 *
 * O input é estrutural pra acomodar diferentes shapes de resposta — listas
 * costumam tipar `profileCompatibility` como `string` cru, enquanto o detalhe
 * já tipa como `FitScoreLevel`. Faz cast pra `FitScoreLevel` na saída.
 */
export const formatFitScoreSummary = (
  raw: FitScoreSummaryResponse | null | undefined
): FitScoreSummary | null => {
  if (!raw) return null

  return {
    level: raw.profileCompatibility as FitScoreLevel,
    approved: raw.approved ?? null
  }
}

/**
 * Formata a resposta crua do endpoint /catalog/v2/products/{id}/fit-score em uma
 * estrutura pronta para consumo pelos componentes, sem lógica de transformação em
 * tempo de render.
 *
 * O cálculo é sempre de uma oferta só. Em idiomas a compatibilidade varia por oferta
 * (frequência, turno, tipo de turma), e quem escolhe qual avaliar é o `offerId` mandado
 * na requisição — sem ele o backend avalia todas e devolve a de maior compatibilidade,
 * identificada em `selectedOffer`.
 */
export const formatProductFitScore = (data: ProductFitScoreResponse): FormattedFitScore | null => {
  const raw = data.fitScore
  if (!raw) return null

  const category = parseFitScoreCategory(raw.category)
  const approved = raw.approved ?? null
  const isApproved = approved === true
  // Aprovação manual do CX promove o perfil pra HIGH em toda a UI (cor, ícone, copy).
  // Derivado uma única vez aqui, para nenhum consumidor replicar a regra.
  const effectiveLevel = approved ? FitScoreLevel.HIGH : raw.profileCompatibility

  return {
    profileCompatibility: raw.profileCompatibility,
    skillsCompatibility: raw.skillsCompatibility,
    category,
    eligibilityStatus: raw.eligibilityStatus ?? null,
    selectedOffer: raw.selectedOffer ?? null,
    effectiveLevel,
    evaluatedSkills: getEvaluatedSkillKeys(raw.skillsCompatibility),
    incompatibleSkills: filterIncompatibleSkills(raw.skillsCompatibility),
    approved,
    isApproved,
    // Único booleano de nível — ver a doc do campo em `FormattedFitScore`.
    showRecommendations: effectiveLevel !== FitScoreLevel.HIGH
  }
}

// Mapeia o código ISO de idioma (vindo do backend) para:
// - enumKey: chave do enum proficientLanguage no i18n (system.entities.enums.proficientLanguage.<KEY>.label)
// - searchQuery: keyword PT usado na busca textual de cursos. A API /v2/user/content/search
//   tem o param `languages` mas o filtro retorna vazio para códigos como "en", daí a busca
//   por texto + categoria LANGUAGE como fallback.
interface LanguageMapping {
  enumKey: string
  searchQuery: string
}

const LANGUAGE_MAP: Record<string, LanguageMapping> = {
  pt: { enumKey: 'PORTUGUESE', searchQuery: 'Português' },
  en: { enumKey: 'ENGLISH', searchQuery: 'Inglês' },
  es: { enumKey: 'SPANISH', searchQuery: 'Espanhol' },
  zh: { enumKey: 'MANDARIN', searchQuery: 'Mandarim' },
  ar: { enumKey: 'ARABIC', searchQuery: 'Árabe' },
  ja: { enumKey: 'JAPANESE', searchQuery: 'Japonês' },
  de: { enumKey: 'GERMAN', searchQuery: 'Alemão' }
}

const resolveLanguage = (code: string | undefined | null): LanguageMapping | null => {
  if (!code) return null
  return LANGUAGE_MAP[code.toLowerCase()] ?? null
}

export const getProficientLanguageEnumKey = (code: string | undefined | null): string | null =>
  resolveLanguage(code)?.enumKey ?? null

export const getLanguageSearchQuery = (code: string | undefined | null): string | null =>
  resolveLanguage(code)?.searchQuery ?? null
